/** Pattern of a color in the form `#rrggbb` (any case). */
const hexColorPattern = /^#[0-9a-f]{6}$/iu;

/**
 * Checks whether a text is a color in the form `#rrggbb`.
 *
 * @param value - The text.
 * @returns `true` for a six-digit hexadecimal color.
 */
export function isHexColor(value: string): boolean {
  const valid = hexColorPattern.test(value);

  return valid;
}

/**
 * Converts one sRGB channel (0 to 255) to its linear value (WCAG 2.2, relative luminance).
 *
 * @param channel - The channel value.
 * @returns The linear value from 0 to 1.
 */
function linearChannel(channel: number): number {
  const value = channel / 255;
  const linear = value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;

  return linear;
}

/**
 * Computes the relative luminance of a color as WCAG 2.2 defines it.
 *
 * @param color - Color `#rrggbb`.
 * @returns The luminance from 0 (black) to 1 (white).
 * @throws RangeError if the color is not of the form `#rrggbb`.
 */
export function relativeLuminance(color: string): number {
  if (!isHexColor(color)) {
    throw new RangeError(`Not a color #rrggbb: ${color}`);
  }

  const red = linearChannel(Number.parseInt(color.slice(1, 3), 16));
  const green = linearChannel(Number.parseInt(color.slice(3, 5), 16));
  const blue = linearChannel(Number.parseInt(color.slice(5, 7), 16));
  const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue;

  return luminance;
}

/**
 * Computes the contrast ratio of two colors as WCAG 2.2 defines it (the same formula as the server
 * check of the employer's accent, `TenantBranding.Contrast`).
 *
 * @param first - Color `#rrggbb`.
 * @param second - Color `#rrggbb`.
 * @returns The ratio from 1 to 21; the order of the colors does not matter.
 * @throws RangeError if a color is not of the form `#rrggbb`.
 */
export function contrastRatio(first: string, second: string): number {
  const firstLuminance = relativeLuminance(first);
  const secondLuminance = relativeLuminance(second);
  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);
  const ratio = (lighter + 0.05) / (darker + 0.05);

  return ratio;
}
